import { writable } from 'svelte/store';
import {
  decideRestoreStep,
  maxScrollable,
  mintScrollKey,
  readScrollKey,
  readScrollPosition,
  RESTORE_DEADLINE_MS,
  saveScrollPosition
} from './scrollRestore';

/**
 * URL-compatible router for the v2 shell.
 *
 * Every URL the old frontend ever handed out must keep working:
 *   /?event=<id>&id=<code>                      ticket deep link (sent to guests)
 *   /?event=&id=&success=true                   Stripe success redirect
 *   /?event=&id=&cancelled=true&reservation=    Stripe cancel redirect
 *   /?invite=<CODE>                             invite redemption
 *   /?admin=true                                admin console
 *   /house (or ?house=true)                     house questionnaire
 *   /?demo=true, /?linkfinder=true              utilities
 */
export type Route =
  | { name: 'home' }
  | { name: 'ticket'; eventId: string; code: string; success: boolean }
  | { name: 'event'; eventId: string }
  | { name: 'community'; slug: string }
  | { name: 'communityManage'; slug: string }
  | { name: 'settings' }
  | { name: 'vidamor' }
  | { name: 'admin' }
  | { name: 'house' }
  | { name: 'demo' }
  | { name: 'linkfinder' };

export interface UrlDirectives {
  inviteCode: string | null;
  cancelledReservationId: string | null;
}

export const route = writable<Route>({ name: 'home' });

/**
 * True when this page load arrived from a Stripe cancel redirect. AppShell
 * releases the hold and wipes the URL params before pages mount, so pages
 * that want to show "checkout cancelled" feedback (e.g. /vidamor) read this
 * store instead of the URL. Additive — the sacred cancel URL is untouched.
 */
export const arrivedFromCancelledCheckout = writable(false);

export function parseLocation(): { route: Route; directives: UrlDirectives } {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get('invite');
  const wasCancelled = params.get('cancelled') === 'true' || params.get('canceled') === 'true';
  if (wasCancelled) arrivedFromCancelledCheckout.set(true);
  const directives: UrlDirectives = {
    inviteCode: invite ? invite.trim().toUpperCase() || null : null,
    cancelledReservationId: wasCancelled ? params.get('reservation') : null
  };

  let parsed: Route = { name: 'home' };
  const eventId = params.get('event');
  const code = params.get('id');
  const manageMatch = window.location.pathname.match(/^\/c\/([^/]+)\/manage\/?$/);
  if (manageMatch) {
    parsed = { name: 'communityManage', slug: decodeURIComponent(manageMatch[1]) };
    return { route: parsed, directives };
  }
  const communityMatch = window.location.pathname.match(/^\/c\/([^/]+)\/?$/);
  if (communityMatch) {
    parsed = { name: 'community', slug: decodeURIComponent(communityMatch[1]) };
    return { route: parsed, directives };
  }
  // Additive (two-lists slice 04): the standing-aware public event page.
  // Placed alongside the other pathname branches, never reordering them —
  // the sacred query-param URLs below still parse exactly as before.
  const eventMatch = window.location.pathname.match(/^\/e\/([^/]+)\/?$/);
  if (eventMatch) {
    parsed = { name: 'event', eventId: decodeURIComponent(eventMatch[1]) };
    return { route: parsed, directives };
  }
  // Additive: the signed-in account settings page (change name, marketing
  // consent). Another pathname branch alongside the others — the sacred
  // query-param URLs below still parse byte-for-byte.
  if (window.location.pathname === '/settings') {
    parsed = { name: 'settings' };
    return { route: parsed, directives };
  }
  // Additive: the standalone, self-contained Vidamor experience. An exact
  // pathname branch alongside the others — the sacred query-param URLs below
  // still parse byte-for-byte, and ?success/?cancelled/?reservation on
  // /vidamor ride along via `directives` (extracted above regardless of path).
  if (window.location.pathname === '/vidamor') {
    parsed = { name: 'vidamor' };
    return { route: parsed, directives };
  }
  if (window.location.pathname === '/house' || params.get('house') === 'true') {
    parsed = { name: 'house' };
  } else if (params.get('demo') === 'true') {
    parsed = { name: 'demo' };
  } else if (params.get('linkfinder') === 'true') {
    parsed = { name: 'linkfinder' };
  } else if (params.get('admin') === 'true') {
    parsed = { name: 'admin' };
  } else if (eventId && code) {
    parsed = { name: 'ticket', eventId, code, success: params.get('success') === 'true' };
  }
  return { route: parsed, directives };
}

// --- Scroll restoration state -----------------------------------------------
// The scroll key of the currently-active history entry. Every history mutation
// must round-trip a scrollKey so back/forward can look up its saved position;
// pushState mints a new key for the new entry, replaceState keeps the current one.
let currentScrollKey = '';
let scrollTicking = false;
/** Cancels an in-flight restore rAF loop; set while one is running. */
let cancelRestore: (() => void) | null = null;

function commit(params: URLSearchParams, path = window.location.pathname, push = false) {
  const query = params.toString();
  const url = query ? `${path}?${query}` : path;
  if (push) {
    // New entry → new key. The old entry keeps its already-recorded position.
    currentScrollKey = mintScrollKey();
    window.history.pushState({ scrollKey: currentScrollKey }, '', url);
  } else {
    // Same entry → preserve its key; the state must never drop it.
    window.history.replaceState({ scrollKey: currentScrollKey }, '', url);
  }
}

/**
 * Forward navigation that pushed a new entry lands at the top of the new page —
 * with the Part A cache the page mounts tall immediately, so without this it
 * would keep the previous page's scroll position. push:false (URL-normalizing
 * replaces) must NOT touch scroll, so callers gate on the pushed flag.
 */
function afterForwardNav(pushed: boolean) {
  if (!pushed) return;
  cancelRestore?.();
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
}

/** rAF-throttled passive recorder for the current entry's scroll position. */
function recordScroll() {
  if (scrollTicking) return;
  scrollTicking = true;
  requestAnimationFrame(() => {
    scrollTicking = false;
    if (currentScrollKey) saveScrollPosition(currentScrollKey, window.scrollY);
  });
}

/** Scroll to top instantly. `behavior: 'instant'` overrides the CSS smooth. */
function scrollToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
}

/**
 * Restore to `targetY` once the document is tall enough to hold it. With the
 * Part A cache the content is tall on the first frame (effectively instant); the
 * rAF loop is the safety net for cache-cold entries. Cancels on any new
 * navigation or on user scroll intent (wheel / touch / keydown).
 */
function restoreScroll(targetY: number) {
  cancelRestore?.();
  if (targetY <= 0) {
    scrollToTop();
    return;
  }
  const deadline = Date.now() + RESTORE_DEADLINE_MS;
  let frame = 0;
  let done = false;

  const stop = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', onIntent);
    window.removeEventListener('touchstart', onIntent);
    window.removeEventListener('keydown', onIntent);
    cancelRestore = null;
  };
  const onIntent = () => stop();
  cancelRestore = stop;

  window.addEventListener('wheel', onIntent, { passive: true });
  window.addEventListener('touchstart', onIntent, { passive: true });
  window.addEventListener('keydown', onIntent);

  const tick = () => {
    if (done) return;
    const step = decideRestoreStep(targetY, maxScrollable(), Date.now() >= deadline);
    if (step.action === 'restore') {
      window.scrollTo({ top: step.y, left: 0, behavior: 'instant' });
      stop();
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
}

function syncFromLocation(event: PopStateEvent) {
  // A navigation is happening: abort any restore loop from a prior popstate.
  cancelRestore?.();
  route.set(parseLocation().route);
  currentScrollKey = readScrollKey(event.state) ?? mintScrollKey();
  restoreScroll(readScrollPosition(currentScrollKey) ?? 0);
}

/** Parse the initial URL, start listening for back/forward, return one-time directives. */
export function initRouter(): UrlDirectives {
  const parsed = parseLocation();
  route.set(parsed.route);

  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // Adopt an existing key (reload / deep link revisit) or mint one, then stamp it
  // into the current entry without changing the URL.
  currentScrollKey = readScrollKey(window.history.state) ?? mintScrollKey();
  window.history.replaceState(
    { ...(typeof window.history.state === 'object' ? window.history.state : {}), scrollKey: currentScrollKey },
    '',
    `${window.location.pathname}${window.location.search}`
  );

  window.addEventListener('popstate', syncFromLocation);
  window.addEventListener('scroll', recordScroll, { passive: true });
  return parsed.directives;
}

export function gotoTicket(eventId: string, code: string, opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  params.set('event', eventId);
  params.set('id', code);
  for (const key of ['success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, '/', push);
  route.set({ name: 'ticket', eventId, code, success: false });
  afterForwardNav(push);
}

export function gotoHome(opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of [
    'event',
    'id',
    'success',
    'cancelled',
    'canceled',
    'reservation',
    'admin',
    'linkfinder',
    'demo',
    'house'
  ]) {
    params.delete(key);
  }
  const push = opts.push ?? false;
  commit(params, '/', push);
  route.set({ name: 'home' });
  afterForwardNav(push);
}

export function gotoEvent(eventId: string, opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, `/e/${encodeURIComponent(eventId)}`, push);
  route.set({ name: 'event', eventId });
  afterForwardNav(push);
}

export function gotoCommunity(slug: string, opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, `/c/${encodeURIComponent(slug)}`, push);
  route.set({ name: 'community', slug });
  afterForwardNav(push);
}

export function gotoCommunityManage(slug: string, opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, `/c/${encodeURIComponent(slug)}/manage`, push);
  route.set({ name: 'communityManage', slug });
  afterForwardNav(push);
}

export function gotoSettings(opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, '/settings', push);
  route.set({ name: 'settings' });
  afterForwardNav(push);
}

export function gotoVidamor(opts: { push?: boolean } = {}) {
  const params = new URLSearchParams(window.location.search);
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation', 'admin']) params.delete(key);
  const push = opts.push ?? true;
  commit(params, '/vidamor', push);
  route.set({ name: 'vidamor' });
  afterForwardNav(push);
}

export function gotoAdmin() {
  const params = new URLSearchParams(window.location.search);
  params.set('admin', 'true');
  for (const key of ['event', 'id', 'success', 'cancelled', 'canceled', 'reservation']) params.delete(key);
  // gotoAdmin replaces in place (no push arg) — same entry, so scroll is untouched.
  commit(params, '/');
  route.set({ name: 'admin' });
}

/** Remove query params in place without changing the active route. */
export function clearUrlParams(...keys: string[]) {
  const params = new URLSearchParams(window.location.search);
  for (const key of keys) params.delete(key);
  commit(params);
}

/** Drop ?success=true after the confirmation has been shown once. */
export function markSuccessSeen() {
  clearUrlParams('success');
  route.update(current => (current.name === 'ticket' ? { ...current, success: false } : current));
}
